import { createFileRoute, notFound } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import {
  ArrowUpRight,
  Download,
  FilePlus2,
  Filter,
  Inbox,
  Menu,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Sparkles,
  Trash2,
  Upload,
} from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { IconButton } from '@/components/ui/icon-button'
import { Input, Textarea } from '@/components/ui/input'
import { Kbd } from '@/components/ui/kbd'
import { Label } from '@/components/ui/label'
import { LoadingState, SkeletonList } from '@/components/ui/loading-state'
import {
  PageBody,
  PageContainer,
  Section,
} from '@/components/ui/page-container'
import { PageHeader, SectionHeader } from '@/components/ui/page-header'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Toolbar,
  ToolbarGroup,
  ToolbarSeparator,
} from '@/components/ui/toolbar'

/**
 * Dev-only design-system gallery. Every Aurora primitive on a single page,
 * sized so Playwright can capture desktop + mobile screenshots for PR
 * evidence. Reachable at `/dev/design-system` in dev builds only — production
 * 404s the route, identical guard pattern to `dev.app-shell-preview.tsx`.
 *
 * Used by `scripts/snapshot-design-system.ts` to produce the screenshots
 * linked in PRs that change Aurora.
 */
export const Route = createFileRoute('/dev/design-system')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound()
  },
  component: DesignSystemPreview,
})

const BUTTON_VARIANTS = [
  'default',
  'secondary',
  'outline',
  'ghost',
  'soft',
  'link',
  'destructive',
] as const

const BUTTON_SIZES = ['sm', 'default', 'lg', 'xl'] as const

const BADGE_TONES = [
  'neutral',
  'primary',
  'success',
  'warning',
  'info',
  'danger',
  'outline',
] as const

const ALERT_TONES = ['info', 'success', 'warning', 'danger', 'neutral'] as const

function DesignSystemPreview() {
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [progress, setProgress] = useState(38)

  // Toggle the dark class on <html> so body::before's parchment gradient and
  // every Tailwind `dark:` variant flip in concert. Restore on unmount so a
  // hot-reload back to a light route doesn't leave the page stuck in dark.
  useEffect(() => {
    const root = document.documentElement
    const was = root.classList.contains('dark')
    if (theme === 'dark') root.classList.add('dark')
    else root.classList.remove('dark')
    return () => {
      if (was) root.classList.add('dark')
      else root.classList.remove('dark')
    }
  }, [theme])

  return (
    <div
      data-testid="design-system-root"
      data-theme={theme}
      className="bg-background min-h-screen"
    >
      <PageContainer>
        <PageHeader
          eyebrow="Aurora"
          title="Design system gallery"
          description="Every shared primitive at a glance. Used to capture the desktop + mobile screenshots that accompany Aurora PRs."
          actions={
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setTheme((t) => (t === 'light' ? 'dark' : 'light'))
                }
                data-testid="theme-toggle"
                aria-pressed={theme === 'dark'}
              >
                {theme === 'dark' ? 'Light mode' : 'Dark mode'}
              </Button>
              <Button size="sm">
                <Sparkles aria-hidden /> Primary action
              </Button>
            </>
          }
        />

        <PageBody>
          <ColorTokensSection />
          <TypographySection />
          <ButtonsSection />
          <IconButtonsSection />
          <BadgesSection />
          <CardsSection />
          <ToolbarSection />
          <FormsSection />
          <AlertsSection />
          <FeedbackSection progress={progress} setProgress={setProgress} />
          <EmptyAndLoadingSection />
        </PageBody>
      </PageContainer>
    </div>
  )
}

function ColorTokensSection() {
  const swatches = [
    { name: 'background', className: 'bg-background border' },
    { name: 'surface', className: 'bg-surface border' },
    { name: 'card', className: 'bg-card border' },
    { name: 'muted', className: 'bg-muted' },
    { name: 'accent', className: 'bg-accent' },
    { name: 'primary', className: 'bg-primary text-primary-foreground' },
    {
      name: 'primary-soft',
      className: 'bg-primary-soft text-primary-soft-foreground',
    },
    { name: 'success', className: 'bg-success text-success-foreground' },
    {
      name: 'success-soft',
      className: 'bg-success-soft text-success-soft-foreground',
    },
    { name: 'warning', className: 'bg-warning text-warning-foreground' },
    {
      name: 'warning-soft',
      className: 'bg-warning-soft text-warning-soft-foreground',
    },
    { name: 'info', className: 'bg-info text-info-foreground' },
    {
      name: 'info-soft',
      className: 'bg-info-soft text-info-soft-foreground',
    },
    {
      name: 'destructive',
      className: 'bg-destructive text-destructive-foreground',
    },
    {
      name: 'destructive-soft',
      className: 'bg-destructive-soft text-destructive-soft-foreground',
    },
    { name: 'border', className: 'bg-border' },
  ]
  return (
    <Section data-testid="section-tokens">
      <SectionHeader
        title="Color tokens"
        description="Semantic colour roles published as Tailwind v4 tokens via @theme inline."
      />
      <Card>
        <CardContent className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {swatches.map((s) => (
            <div
              key={s.name}
              className={`flex h-16 flex-col justify-end rounded-md px-3 py-2 text-xs font-medium ${s.className}`}
            >
              {s.name}
            </div>
          ))}
        </CardContent>
      </Card>
    </Section>
  )
}

function TypographySection() {
  return (
    <Section data-testid="section-typography">
      <SectionHeader
        title="Typography"
        description="Display ramp tightens tracking on h1/h2/h3 via the global base layer."
      />
      <Card>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <p className="aurora-eyebrow">Eyebrow</p>
            <h1 className="text-[1.75rem] font-semibold leading-[1.1] tracking-tight">
              Page title — 28px display
            </h1>
            <h2 className="text-base font-semibold tracking-tight">
              Section heading — 16px
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Body copy at 14px with the muted-foreground token, suitable for
              page descriptions and the lede beneath a PageHeader.
            </p>
            <p className="text-xs text-muted-foreground/80">
              Caption — 12px / muted-foreground/80
            </p>
          </div>
          <div className="border-t border-border pt-4 text-sm">
            <p>
              Inline elements: <Kbd>⌘</Kbd>
              <Kbd>K</Kbd> to search, or jump straight to a{' '}
              {/* eslint-disable-next-line no-restricted-syntax */}
              <a
                className="text-primary underline-offset-4 hover:underline"
                href="https://docfiller.aiescapelabs.com"
              >
                primary link
              </a>
              .
            </p>
          </div>
        </CardContent>
      </Card>
    </Section>
  )
}

function ButtonsSection() {
  return (
    <Section data-testid="section-buttons">
      <SectionHeader
        title="Buttons"
        description="7 variants × 4 sizes. Default size is 40px tall to hit the 40px touch target."
      />
      <Card>
        <CardContent className="space-y-5">
          {BUTTON_SIZES.map((size) => (
            <div key={size} className="space-y-2">
              <p className="aurora-eyebrow">Size: {size}</p>
              <div className="flex flex-wrap items-center gap-2">
                {BUTTON_VARIANTS.map((variant) => (
                  <Button key={variant} variant={variant} size={size}>
                    {variant}
                  </Button>
                ))}
              </div>
            </div>
          ))}
          <div className="space-y-2">
            <p className="aurora-eyebrow">With icon / disabled / loading</p>
            <div className="flex flex-wrap items-center gap-2">
              <Button>
                <Plus aria-hidden /> New document
              </Button>
              <Button variant="outline">
                <Download aria-hidden /> Download
              </Button>
              <Button variant="ghost">
                Read more <ArrowUpRight aria-hidden />
              </Button>
              <Button disabled>Disabled</Button>
              <Button aria-invalid>aria-invalid</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </Section>
  )
}

function IconButtonsSection() {
  return (
    <Section data-testid="section-icon-buttons">
      <SectionHeader
        title="Icon buttons"
        description="Accessible wrapper around <Button size='icon'>. Required label becomes both aria-label and hidden screen-reader text."
      />
      <Card>
        <CardContent className="flex flex-wrap items-center gap-3">
          <IconButton label="Open menu" variant="ghost" size="icon-sm">
            <Menu />
          </IconButton>
          <IconButton label="Search" variant="ghost">
            <Search />
          </IconButton>
          <IconButton label="More actions" variant="outline">
            <MoreHorizontal />
          </IconButton>
          <IconButton label="Refresh" variant="secondary">
            <RefreshCw />
          </IconButton>
          <IconButton label="Settings" variant="soft" size="icon-lg">
            <Settings />
          </IconButton>
          <IconButton label="Delete document" variant="destructive">
            <Trash2 />
          </IconButton>
        </CardContent>
      </Card>
    </Section>
  )
}

function BadgesSection() {
  return (
    <Section data-testid="section-badges">
      <SectionHeader
        title="Badges"
        description="Soft + solid status pills."
      />
      <Card>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {BADGE_TONES.map((tone) => (
              <Badge key={tone} tone={tone}>
                {tone}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {BADGE_TONES.filter((t) => t !== 'outline').map((tone) => (
              <Badge key={tone} tone={tone} variant="solid">
                {tone}
              </Badge>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Badge size="xs" tone="warning">
              suggested
            </Badge>
            <Badge size="sm" tone="success">
              applied
            </Badge>
            <Badge size="md" tone="primary">
              live preview
            </Badge>
          </div>
        </CardContent>
      </Card>
    </Section>
  )
}

function CardsSection() {
  return (
    <Section data-testid="section-cards">
      <SectionHeader title="Card surfaces" description="Default, muted, outline. Interactive cards lift on hover." />
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>Default card</CardTitle>
            <CardDescription>White, raised 1px shadow.</CardDescription>
            <CardAction>
              <IconButton label="Open" variant="ghost" size="icon-sm">
                <ArrowUpRight />
              </IconButton>
            </CardAction>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Cards default to a `shadow-soft` raise that's identical to
              the rest of the chrome — buttons, inputs, the AppShell.
            </p>
          </CardContent>
          <CardFooter>
            <Button size="sm" variant="outline">
              Action
            </Button>
          </CardFooter>
        </Card>
        <Card tone="muted">
          <CardHeader>
            <CardTitle>Muted card</CardTitle>
            <CardDescription>Embedded into the page.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Use when the card should read as part of the page surface
              rather than a raised affordance.
            </p>
          </CardContent>
        </Card>
        <Card tone="outline" interactive>
          <CardHeader>
            <CardTitle>Outline · interactive</CardTitle>
            <CardDescription>Hover lifts. Used for clickable rows.</CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Pair `tone="outline"` with `interactive` on whole-card link
              targets so the hover affords interactivity.
            </p>
          </CardContent>
        </Card>
      </div>
    </Section>
  )
}

function ToolbarSection() {
  return (
    <Section data-testid="section-toolbar">
      <SectionHeader
        title="Toolbar / action group"
        description="Stacks on narrow viewports, single row on sm+. Hosts mixed buttons and icon buttons."
      />
      <Card tone="outline">
        <CardContent className="space-y-5">
          <Toolbar aria-label="Documents toolbar">
            <ToolbarGroup>
              <Button>
                <Upload aria-hidden /> Upload
              </Button>
              <Button variant="outline">
                <FilePlus2 aria-hidden /> New form
              </Button>
            </ToolbarGroup>
            <ToolbarGroup position="trailing">
              <Button variant="ghost" size="sm">
                <Filter aria-hidden /> Filter
              </Button>
              <ToolbarSeparator />
              <IconButton label="Refresh" variant="ghost">
                <RefreshCw />
              </IconButton>
              <IconButton label="Settings" variant="ghost">
                <Settings />
              </IconButton>
            </ToolbarGroup>
          </Toolbar>
          <Toolbar aria-label="Selection toolbar" align="row">
            <ToolbarGroup>
              <IconButton label="Search" variant="ghost" size="icon-sm">
                <Search />
              </IconButton>
              <IconButton label="More" variant="ghost" size="icon-sm">
                <MoreHorizontal />
              </IconButton>
            </ToolbarGroup>
            <ToolbarGroup position="trailing">
              <IconButton label="Delete" variant="ghost" size="icon-sm">
                <Trash2 />
              </IconButton>
            </ToolbarGroup>
          </Toolbar>
        </CardContent>
      </Card>
    </Section>
  )
}

function FormsSection() {
  return (
    <Section data-testid="section-forms">
      <SectionHeader
        title="Inputs"
        description="Input + Textarea + Label, all sharing the same focus ring."
      />
      <Card>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ds-name">Recipient</Label>
            <Input id="ds-name" placeholder="Alice Nguyen" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ds-email">Email</Label>
            <Input
              id="ds-email"
              type="email"
              aria-invalid
              defaultValue="not-an-email"
            />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="ds-notes">Notes</Label>
            <Textarea
              id="ds-notes"
              placeholder="Free-form context for the agent…"
            />
          </div>
        </CardContent>
      </Card>
    </Section>
  )
}

function AlertsSection() {
  return (
    <Section data-testid="section-alerts">
      <SectionHeader
        title="Alerts"
        description="Inline callouts for the page surface. Use ToastsErrorState pattern for transient notifications."
      />
      <div className="grid gap-3 md:grid-cols-2">
        {ALERT_TONES.map((tone) => (
          <Alert key={tone} tone={tone}>
            <AlertTitle>
              {tone === 'danger'
                ? 'Upload failed'
                : tone === 'warning'
                  ? 'Document unsigned'
                  : tone === 'success'
                    ? 'Filled successfully'
                    : tone === 'info'
                      ? 'Did you know?'
                      : 'Neutral notice'}
            </AlertTitle>
            <AlertDescription>
              {tone === 'danger'
                ? 'The R2 upload returned a 502. Try again or check the network panel.'
                : tone === 'warning'
                  ? "We pre-filled the form, but the signature field is still blank."
                  : tone === 'success'
                    ? "All 12 fields applied. Download the completed PDF below."
                    : tone === 'info'
                      ? 'You can save workspace context once and re-use it across uploads.'
                      : 'This is a neutral notice without semantic tone.'}
            </AlertDescription>
          </Alert>
        ))}
        <Alert
          tone="info"
          variant="outline"
          className="md:col-span-2"
          action={
            <Button size="sm" variant="outline">
              Learn more
            </Button>
          }
        >
          <AlertTitle>Outline variant</AlertTitle>
          <AlertDescription>
            Outline alerts sit on the white card surface — useful inside a
            dialog body so the alert doesn't clash with the dialog's own
            background.
          </AlertDescription>
        </Alert>
      </div>
    </Section>
  )
}

function FeedbackSection({
  progress,
  setProgress,
}: {
  progress: number
  setProgress: (n: number) => void
}) {
  return (
    <Section data-testid="section-feedback">
      <SectionHeader
        title="Progress & feedback"
        description="Determinate + indeterminate progress, skeletons."
      />
      <Card>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span>Filling fields…</span>
              <span className="text-muted-foreground">{progress}%</span>
            </div>
            <Progress percent={progress} />
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => setProgress(0)}>
                Reset
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgress(Math.min(100, progress + 12))}
              >
                +12%
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProgress(100)}
              >
                Finish
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <p className="aurora-eyebrow">Indeterminate</p>
            <Progress percent={null} />
          </div>
          <div className="space-y-2">
            <p className="aurora-eyebrow">Skeletons</p>
            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-10 w-24" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Section>
  )
}

function EmptyAndLoadingSection() {
  return (
    <Section data-testid="section-empty-loading">
      <SectionHeader
        title="Empty & loading states"
        description="Paired primitives for the two common 'no content yet' cases."
      />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardContent>
            <EmptyState
              icon={Inbox}
              title="No documents yet"
              description="Upload a PDF or DOCX above and the agent will pre-fill it for review."
              action={
                <Button>
                  <Upload aria-hidden /> Upload your first document
                </Button>
              }
            />
          </CardContent>
        </Card>
        <Card>
          <CardContent className="space-y-4">
            <LoadingState
              title="Fetching documents…"
              description="Reading from R2 — this usually takes a second."
            />
            <div className="border-t border-border pt-4">
              <p className="aurora-eyebrow mb-2">SkeletonList</p>
              <SkeletonList rows={3} />
            </div>
            <div className="border-t border-border pt-4">
              <LoadingState
                variant="inline"
                title="Saving…"
                description="autosave in 1s"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </Section>
  )
}
